import React, {
  forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState
} from 'react';
import {
  completeShortcode, rememberRecent, searchEmoji, shortcodeAt
} from '../lib/emoji';
import EmojiPicker, { Smiley } from './EmojiPicker';
import {
  MAX_CHAT_FILE_BYTES, Paperclip, fileKind, fileSize
} from './ChatFile';
import { MicButton, VoiceBar, canRecord, useVoiceRecorder } from './VoiceNote';
import Avatar from './Avatar';
import { hueOf } from '../lib/initials';
import { mentionAt, mentionsIn, matchPeople } from '../lib/mentions';

/**
 * THE BOX AT THE FOOT OF A CONVERSATION - the words, a smiley, a paperclip, a
 * microphone, Send - as one component, because there are two of them now:
 * the conversation's own, and a branch's (Gordon, 23 October: answers to one
 * message, in a panel beside the conversation). Lifted out of ChatPage.js
 * without changing what it does: Enter sends and Shift+Enter is a new line
 * (except on a phone), ":thu" offers emoji, a file waits above the box until
 * Send, a pasted screenshot is a file, a voice note goes as soon as it is
 * finished, and the words wait for you when you move away and come back
 * (one draft per `place`, kept in memory only).
 *
 * WHAT IS NEW IN IT is the @ picker: "@" at the start of a word offers the
 * people who can see this conversation (`people`, plus "everyone" when
 * `everyone` says this person may), filtered as you type, worked by the
 * keyboard (up, down, Enter or Tab, Escape) or a tap; the name goes in as
 * "@Rita Gatt". What is mentioned is worked out from the words when it is
 * sent (lib/mentions), so a name deleted again is not sent as a mention.
 *
 * The page decides what sending MEANS (`onSend`), because that is different
 * everywhere - a ticket's file goes to the ticket, a branch reply to its
 * branch. It is handed { body, file, voice, mentions } and the composer's own
 * `ui` ({ clearFile, fileTrouble }), and throws to say it failed: with
 * `fileTrouble` for the line beside the box, `bodySaid` when the words did go,
 * so they are not put back.
 *
 * `id` names the box (ch-draft for the conversation's, so every link and test
 * that finds it still does), and everything inside takes its ids from it.
 */

const enterSends = () => {
  try {
    return !window.matchMedia('(pointer: coarse)').matches;
  } catch {
    return true;
  }
};

const Composer = forwardRef(function Composer({
  id = 'ch-draft', place, placeholder, placeholderWithFile = 'Add a message (optional)…',
  onSend, fileNote = null, clipTitle = 'Attach a file', label = 'Message',
  people = [], everyone = false, mentionsOn = false, onEditLast = null,
  above = null, extra = null, hint = 'Enter sends. Shift and Enter starts a new line.'
}, ref) {
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [fileTrouble, setFileTrouble] = useState(null);
  const [codes, setCodes] = useState(null);
  const [who, setWho] = useState(null);
  const [picker, setPicker] = useState(false);
  const box = useRef(null);
  const fileInput = useRef(null);
  const smileyBtn = useRef(null);
  const waved = useRef(null);
  const wavedAt = useRef(null);
  const caretTo = useRef(null);
  const drafts = useRef(new Map());
  const draftRef = useRef('');
  const lastPlace = useRef(place);

  useEffect(() => { draftRef.current = draft; }, [draft]);

  /*
   * Moving to another conversation: a chosen file does not follow you (sent on
   * a ticket it would land in the wrong, permanent, place), and neither does
   * an open picker or list. The words DO wait for you.
   */
  useEffect(() => {
    setFile(null);
    setFileTrouble(null);
    setPicker(false);
    setCodes(null);
    setWho(null);
    drafts.current.set(lastPlace.current, draftRef.current);
    lastPlace.current = place;
    const kept = drafts.current.get(place) || '';
    draftRef.current = kept;
    setDraft(kept);
  }, [place]);

  // The box grows with what is in it, to about six lines, then scrolls.
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight + 2}px`;
    if (caretTo.current !== null) {
      const at = Math.min(caretTo.current, el.value.length);
      caretTo.current = null;
      try { el.setSelectionRange(at, at); } catch { /* not a text box any more */ }
    }
  }, [draft, place]);

  /** A file from the paperclip, a drop or a paste: checked, then held until Send. */
  const choose = (picked) => {
    if (!picked) return;
    if (picked.size > MAX_CHAT_FILE_BYTES) {
      setFileTrouble(`${picked.name} is ${fileSize(picked.size)}. Files can be up to `
        + `${MAX_CHAT_FILE_BYTES / 1024 / 1024} MB.`);
      return;
    }
    if (picked.size === 0) {
      setFileTrouble(`${picked.name} is empty, so there is nothing to send.`);
      return;
    }
    setFileTrouble(null);
    setFile(picked);
  };

  const candidates = everyone ? [{ key: 'everyone', name: 'everyone', everyone: true }, ...people] : people;

  const send = async (event, voice) => {
    if (event && event.preventDefault) event.preventDefault();
    const body = draft.trim();
    const attached = voice || file;
    if ((!body && !attached) || sending) return;
    setSending(true);
    setUploading(Boolean(attached));
    setFileTrouble(null);
    setDraft('');
    setWho(null);
    try {
      await onSend({
        body, file: attached, voice: Boolean(voice),
        mentions: mentionsOn ? mentionsIn(body, candidates) : []
      }, { clearFile: () => setFile(null), fileTrouble: setFileTrouble });
      if (attached && !voice) setFile(null);
    } catch (err) {
      if (err?.fileTrouble) setFileTrouble(err.fileTrouble);
      // A recording cannot be made again: it waits above the box to be sent again.
      if (voice && err?.keepVoice !== false) setFile(voice);
      if (body && !err?.bodySaid) setDraft(body);
    } finally {
      setSending(false);
      setUploading(false);
    }
  };

  const sendVoice = (recorded) => {
    if (!recorded || !recorded.size) {
      setFileTrouble('Nothing was recorded, so there is nothing to send.');
      return;
    }
    if (recorded.size > MAX_CHAT_FILE_BYTES) {
      setFileTrouble(`That voice note is ${fileSize(recorded.size)}. Files can be up to `
        + `${MAX_CHAT_FILE_BYTES / 1024 / 1024} MB.`);
      return;
    }
    send(null, recorded);
  };
  const recorder = useVoiceRecorder({ onRecorded: sendVoice, onError: setFileTrouble, resetKey: place });
  const [mayRecord] = useState(canRecord);
  const recording = recorder.recording;

  /** Text put in from here (an emoji, a name) goes where the cursor was. */
  const insertText = (text, { focus = true } = {}) => {
    const el = box.current;
    const cur = draftRef.current;
    const start = el && typeof el.selectionStart === 'number' ? el.selectionStart : cur.length;
    const end = el && typeof el.selectionEnd === 'number' ? el.selectionEnd : start;
    const next = cur.slice(0, start) + text + cur.slice(end);
    caretTo.current = start + text.length;
    draftRef.current = next;
    setDraft(next);
    setCodes(null);
    if (focus && el) el.focus({ preventScroll: true });
  };

  useImperativeHandle(ref, () => ({
    take: choose,
    focus: () => box.current?.focus({ preventScroll: true }),
    insert: insertText,
    busy: () => sending
  }));

  /* ------------------------------------------------- ":thu", and "@Ri" */

  const lookForCode = (value, caret) => {
    const at = shortcodeAt(value, caret);
    if (!at) { waved.current = null; setCodes(null); return; }
    if (waved.current === at.start) { setCodes(null); return; }
    const items = searchEmoji(at.query, 6);
    setCodes((was) => (items.length === 0 ? null : {
      ...at, items, active: was && was.start === at.start && was.query === at.query ? was.active : 0
    }));
  };

  const lookForName = (value, caret) => {
    if (!mentionsOn) return;
    const at = mentionAt(value, caret);
    if (!at) { wavedAt.current = null; setWho(null); return; }
    if (wavedAt.current === at.start) { setWho(null); return; }
    const items = matchPeople(candidates, at.query, 6);
    setWho((was) => (items.length === 0 ? null : {
      ...at, items, active: was && was.start === at.start ? Math.min(was.active, items.length - 1) : 0
    }));
  };

  const look = (value, caret) => { lookForCode(value, caret); lookForName(value, caret); };

  const pickCode = (item) => {
    if (!item || !codes) return;
    const el = box.current;
    const cur = draftRef.current;
    const end = el && typeof el.selectionStart === 'number' ? el.selectionStart : cur.length;
    const next = cur.slice(0, codes.start) + item.char + cur.slice(end);
    caretTo.current = codes.start + item.char.length;
    draftRef.current = next;
    setDraft(next);
    setCodes(null);
    rememberRecent(item.char);
  };

  /** One of the @ list picked: "@Ri" becomes "@Rita Gatt ". */
  const pickName = (person) => {
    if (!person || !who) return;
    const el = box.current;
    const cur = draftRef.current;
    const end = el && typeof el.selectionStart === 'number' ? el.selectionStart : cur.length;
    const said = `@${person.name} `;
    const next = cur.slice(0, who.start) + said + cur.slice(end);
    caretTo.current = who.start + said.length;
    draftRef.current = next;
    setDraft(next);
    setWho(null);
    if (el) el.focus({ preventScroll: true });
  };

  const typed = (e) => {
    const { value } = e.target;
    const caret = e.target.selectionStart;
    const done = completeShortcode(value, caret);
    if (done) {
      caretTo.current = done.caret;
      setDraft(done.text);
      setCodes(null);
      return;
    }
    setDraft(value);
    look(value, caret);
  };

  /** Up, down, Enter, Tab and Escape, for whichever list is up. */
  const workList = (e, list, set, pick, wave) => {
    const n = list.items.length;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const step = e.key === 'ArrowDown' ? 1 : -1;
      set((c) => (c ? { ...c, active: (c.active + step + n) % n } : c));
      return true;
    }
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      pick(list.items[list.active]);
      return true;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      wave.current = list.start;
      set(null);
      return true;
    }
    return false;
  };

  const cannotSend = sending || (!draft.trim() && !file);

  const keyDown = (e) => {
    if (!e.nativeEvent.isComposing) {
      if (who && workList(e, who, setWho, pickName, wavedAt)) return;
      if (codes && workList(e, codes, setCodes, pickCode, waved)) return;
    }
    // Up in an empty box edits my last message, as Slack does.
    if (e.key === 'ArrowUp' && onEditLast && !draft && !file && !e.shiftKey && !e.altKey
      && !e.ctrlKey && !e.metaKey) {
      if (onEditLast()) e.preventDefault();
      return;
    }
    if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) return;
    if (!enterSends()) return;
    e.preventDefault();
    if (!cannotSend) send();
  };

  const listId = `${id}-codes`;
  const whoId = `${id}-who`;
  const codeList = codes && (
    <ul className="ch-codes" id={id === 'ch-draft' ? 'ch-codes' : listId} role="listbox" aria-label="Emoji suggestions">
      {codes.items.map((item, i) => (
        <li
          key={item.code}
          id={`${id === 'ch-draft' ? 'ch-code' : `${id}-code`}-${i}`}
          role="option"
          aria-selected={i === codes.active}
          className="ch-code"
          onMouseDown={(e) => { e.preventDefault(); pickCode(item); }}
          onMouseEnter={() => setCodes((c) => (c ? { ...c, active: i } : c))}
        >
          <span className="ch-code-emoji" aria-hidden="true">{item.char}</span>
          <span className="ch-code-name">:{item.code}:</span>
        </li>
      ))}
    </ul>
  );

  const whoList = who && (
    <ul className="ch-codes ch-mention-list" id={whoId} role="listbox" aria-label="People to mention">
      {who.items.map((p, i) => (
        <li
          key={p.key}
          id={`${whoId}-${i}`}
          role="option"
          aria-selected={i === who.active}
          className="ch-code ch-mention-option"
          onMouseDown={(e) => { e.preventDefault(); pickName(p); }}
          onMouseEnter={() => setWho((c) => (c ? { ...c, active: i } : c))}
        >
          {p.everyone
            ? <span className="ch-avatar ch-mention-av ch-mention-all" aria-hidden="true">@</span>
            : <Avatar person={p} className="ch-avatar ch-mention-av" data-hue={hueOf(p.key)} aria-hidden="true" />}
          <span className="ch-code-name">
            {p.everyone ? '@everyone' : p.name}
            {p.everyone && <span className="ch-mention-note"> · everybody in the group</span>}
          </span>
        </li>
      ))}
    </ul>
  );

  const activeDescendant = who ? `${whoId}-${who.active}`
    : (codes ? `${id === 'ch-draft' ? 'ch-code' : `${id}-code`}-${codes.active}` : undefined);

  return (
    <>
      {fileTrouble && <p className="ch-attach-error" role="alert">{fileTrouble}</p>}
      {file && (
        <div className="ch-pending">
          <span className="ch-file-kind" aria-hidden="true">{fileKind(file.type)}</span>
          <span className="ch-file-text">
            <span className="ch-file-name">{file.name}</span>
            <span className="ch-file-size">
              {fileSize(file.size)}
              {fileNote ? ` · ${fileNote}` : ''}
            </span>
          </span>
          <button
            type="button"
            className="ch-pending-remove"
            aria-label={`Remove ${file.name}`}
            disabled={sending}
            onClick={() => setFile(null)}
          >
            ×
          </button>
        </div>
      )}
      <input
        ref={fileInput}
        id={id === 'ch-draft' ? 'ch-file' : `${id}-file`}
        type="file"
        className="ch-file-input"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => {
          choose(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
      {above}
      <form className="ch-say" onSubmit={send}>
        {!recording && (
          <button
            ref={smileyBtn}
            type="button"
            className="ch-emoji-open"
            aria-label="Add an emoji"
            title="Add an emoji"
            aria-haspopup="dialog"
            aria-expanded={picker}
            disabled={sending}
            onClick={() => setPicker((p) => !p)}
          >
            <Smiley />
          </button>
        )}
        {!recording && (
          <button
            type="button"
            className="ch-attach"
            aria-label="Attach a file"
            title={clipTitle}
            disabled={sending}
            onClick={() => fileInput.current && fileInput.current.click()}
          >
            <Paperclip />
          </button>
        )}
        {mayRecord && !recording && <MicButton recorder={recorder} disabled={sending} />}
        <label className="ch-say-label" htmlFor={id}>{label}</label>
        {recording ? <VoiceBar recorder={recorder} /> : (
          <textarea
            ref={box}
            id={id}
            className="ch-draft"
            rows={1}
            value={draft}
            placeholder={file ? placeholderWithFile : placeholder}
            aria-describedby={`${id}-hint`}
            aria-autocomplete="list"
            aria-controls={who ? whoId : (codes ? (id === 'ch-draft' ? 'ch-codes' : listId) : undefined)}
            aria-activedescendant={activeDescendant}
            onChange={typed}
            onKeyDown={keyDown}
            onClick={(e) => look(e.target.value, e.target.selectionStart)}
            onKeyUp={(e) => {
              if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) {
                look(e.target.value, e.target.selectionStart);
              }
            }}
            onBlur={() => { setCodes(null); setWho(null); }}
            onPaste={(e) => {
              const pasted = e.clipboardData?.files?.[0];
              if (!pasted) return;
              e.preventDefault();
              choose(pasted);
            }}
          />
        )}
        <span id={`${id}-hint`} className="ch-say-label">{hint}</span>
        {!recording && (
          <button type="submit" disabled={cannotSend}>{uploading ? 'Uploading…' : 'Send'}</button>
        )}
        {extra}
        {codeList}
        {whoList}
      </form>
      {picker && !recording && (
        <EmojiPicker
          key="compose"
          mode="compose"
          anchor={smileyBtn.current}
          label="Add an emoji"
          onPick={(char, { keepOpen }) => {
            insertText(char, { focus: !keepOpen });
            if (!keepOpen) setPicker(false);
          }}
          onClose={(why) => {
            setPicker(false);
            if (why === 'escape' && box.current) box.current.focus({ preventScroll: true });
          }}
        />
      )}
    </>
  );
});

export default Composer;
